import { useState, useEffect, useMemo, useCallback } from 'react';

interface UseTimeManagerProps {
  mode?: 'current' | 'relative';
  timestamp?: string | null;
  updateInterval?: number;
  format?: 'full' | 'short';
  businessTimezone?: string;
}

interface UseTimeManagerReturn {
  currentTime: Date;
  formattedTime: string;
  formatRelativeTime: (timestamp: string) => string;
  formatCurrentTime: (format?: 'full' | 'short') => string;
}

export const useTimeManager = ({
  mode = 'current',
  timestamp = null,
  updateInterval = 60000,
  format = 'short',
  businessTimezone
}: UseTimeManagerProps = {}): UseTimeManagerReturn => {
  // Determine the timezone to use - no country code fallback
  const effectiveTimezone = useMemo(() => {
    if (businessTimezone) return businessTimezone;
    return Intl.DateTimeFormat().resolvedOptions().timeZone; // Fallback to browser timezone
  }, [businessTimezone]);

  // Store actual UTC time - never manipulate the Date object itself
  const [currentTime, setCurrentTime] = useState(new Date());

  // Relative time formatting function
  const formatRelativeTime = useCallback((timestamp: string): string => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - messageTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 24 * 60) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInMinutes / (24 * 60));
      return `${days}d ago`;
    }
  }, []);

  // Current time formatting function - uses business timezone for display
  const formatCurrentTime = useCallback((formatType: 'full' | 'short' = format): string => {
    try {
      if (formatType === 'full') {
        return currentTime.toLocaleString('en-US', {
          timeZone: effectiveTimezone
        });
      }
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: effectiveTimezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.warn('Failed to format time in business timezone:', error);
      // Fallback to browser timezone
      return formatType === 'full'
        ? currentTime.toLocaleString()
        : currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  }, [currentTime, format, effectiveTimezone]);

  // Main time update effect - simple UTC time update
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, updateInterval);

    return () => clearInterval(interval);
  }, [updateInterval]);

  // Computed formatted time based on mode
  const formattedTime = useMemo((): string => {
    if (mode === 'relative') {
      if (!timestamp) {
        return 'No messages';
      }
      return formatRelativeTime(timestamp);
    } else {
      return formatCurrentTime();
    }
  }, [mode, timestamp, formatRelativeTime, formatCurrentTime]);

  return {
    currentTime,
    formattedTime,
    formatRelativeTime,
    formatCurrentTime,
  };
};

// Backward compatibility hooks
export const useCurrentTime = () => {
  const { currentTime } = useTimeManager({ mode: 'current' });
  return { currentTime };
};

export const useLiveTimestamp = (timestamp: string | null, updateInterval: number = 60000) => {
  const { formattedTime } = useTimeManager({
    mode: 'relative',
    timestamp,
    updateInterval
  });
  return formattedTime;
};

// Convenience hooks for common use cases
export const useCurrentTimeFormatted = (format: 'full' | 'short' = 'short') => {
  const { formattedTime } = useTimeManager({ mode: 'current', format });
  return formattedTime;
};

export const useRelativeTime = (timestamp: string | null) => {
  const { formattedTime } = useTimeManager({ mode: 'relative', timestamp });
  return formattedTime;
};